if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.syncInitialVariantUrl();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, selectedVariantId, selectedVariant } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const normalizedOptionValues = Array.isArray(selectedOptionValues)
          ? selectedOptionValues.filter(Boolean)
          : [];

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        if (selectedVariant?.id) {
          this.updateVariantInputs(selectedVariant.id);
          this.updateSelectedVariantScript(selectedVariant);
        } else if (selectedVariantId) {
          this.updateVariantInputs(selectedVariantId);
        }

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            normalizedOptionValues,
            shouldFetchFullPage,
            selectedVariantId
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        // In-memory variant section cache populated by
        // assets/product-variant-section-preload.js during idle. Shopify's
        // section responses carry Cache-Control: private, no-store
        // (Set-Cookie forces it) so the browser HTTP cache can't hold
        // them — we keep our own. A cache hit makes variant switching
        // effectively instant: Map.get + DOMParser, ~5ms total instead
        // of a 150-300ms Section Rendering API roundtrip.
        const cached = window.__variantSectionCache?.get?.(requestUrl);
        if (cached) {
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(cached, 'text/html');
          callback(html);
          return;
        }

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            // Populate the cache for any subsequent re-click of the same
            // variant (e.g. customer toggles back). Same Map the preload
            // writes to. Length guard skips empty / error responses; content
            // guard skips 200 responses that aren't actually section renders
            // (password page, throttling soft-page, login redirect, error
            // template) — caching one would blank the PDP on the next cache
            // hit when querySelector('product-info') returns null and the
            // viewTransition hides the live element while inserting nothing.
            if (
              window.__variantSectionCache &&
              responseText &&
              responseText.length > 100 &&
              responseText.includes('<product-info')
            ) {
              window.__variantSectionCache.set(requestUrl, responseText);
            }
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            if (!targetId) return;
            const focusTarget = document.querySelector(`#${targetId}`);
            if (!focusTarget || typeof focusTarget.focus !== 'function') return;

            const skipScrollFocus =
              typeof window !== 'undefined' && window.matchMedia?.('(hover: none) and (pointer: coarse)')?.matches;
            if (skipScrollFocus) return;

            try {
              focusTarget.focus({ preventScroll: true });
            } catch (error) {
              focusTarget.focus();
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      syncInitialVariantUrl() {
        const initialVariant = this.getSelectedVariant(this);
        if (!initialVariant?.id) return;

        const currentLocation = `${window.location.pathname}${window.location.search}`;
        this.updateURL(currentLocation, initialVariant.id);
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, variantId = null) {
        const parsedUrl = new URL(url, window.location.origin);

        if (!shouldFetchFullPage) {
          parsedUrl.searchParams.set('section_id', this.sectionId);
        } else {
          parsedUrl.searchParams.delete('section_id');
        }

        if (variantId) {
          parsedUrl.searchParams.set('variant', variantId);
          parsedUrl.searchParams.delete('option_values');
        } else if (optionValues.length) {
          parsedUrl.searchParams.set('option_values', optionValues.join(','));
          parsedUrl.searchParams.delete('variant');
        } else {
          parsedUrl.searchParams.delete('variant');
          parsedUrl.searchParams.delete('option_values');
        }

        return `${parsedUrl.pathname}${parsedUrl.search}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          // ---------- CRITICAL PATH (sync, minimal) ----------
          // Only what the customer's eye / next interaction actually depends on:
          //   1. URL — back-button + share-link correctness
          //   2. variant id in form — ATC reads from here
          //   3. variant image — visible confirmation the variant changed
          //   4. submit button state — out-of-stock handling
          //
          // Everything else is deferred (see DEFERRED block below). For
          // same-product variant switches the pill already shows :checked
          // state from the browser's native handling, and on JNCO the
          // price is invariant across sizes within a single product, so
          // deferring the #price innerHTML rewrite is invisible to the
          // customer. SKU / Inventory / pickup widget / quantity rules
          // / variantChange subscribers are all secondary text fields
          // not on the critical visual path.
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const addButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const shouldDisableAddButton = addButton?.hasAttribute('disabled') ?? true;
          const buttonText = shouldDisableAddButton
            ? (window.variantStrings?.soldOut)
            : (window.variantStrings?.addToCart);
          this.productForm?.toggleSubmitButton(shouldDisableAddButton, buttonText);

          // ---------- DEFERRED (requestIdleCallback) ----------
          // Token guard makes rapid clicks safe: when a newer click bumps
          // _variantUpdateToken, the older deferred work bails so we don't
          // commit stale-variant text to the DOM. Critical path always
          // runs synchronously; only the trailing work is interruptible.
          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          const myToken = (this._variantUpdateToken = (this._variantUpdateToken || 0) + 1);
          const deferredWork = () => {
            if (myToken !== this._variantUpdateToken) return;
            this.pickupAvailability?.update(variant);
            this.updateOptionValues(html);
            updateSourceFromDestination('price');
            updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
            updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
            updateSourceFromDestination('Volume');
            updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
            this.updateQuantityRules(this.sectionId, html);
            this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
            this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');
            const priceDestination = this.querySelector(`#price-${this.dataset.section}`);
            if (priceDestination) priceDestination.classList.remove('hidden');
            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            });
          };

          if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(deferredWork, { timeout: 200 });
          } else {
            setTimeout(deferredWork, 0);
          }
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          const value = variantId ? String(variantId) : '';
          input.value = value;
          input.setAttribute('value', value);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateSelectedVariantScript(variant) {
        const script = this.variantSelectors?.querySelector('[data-selected-variant]');
        if (!script) return;

        try {
          script.textContent = JSON.stringify(variant);
        } catch (error) {
          console.error('Failed to serialize selected variant', error);
        }
      }

      updateURL(url, variantId) {
        const parsedUrl = new URL(url, window.location.origin);
        const preserveParams = new URLSearchParams(parsedUrl.search);
        preserveParams.delete('option_values');
        if (variantId) {
          preserveParams.set('variant', variantId);
        } else {
          preserveParams.delete('variant');
        }

        const relativePath = `${parsedUrl.pathname}${preserveParams.toString() ? `?${preserveParams.toString()}` : ''}`;

        const shareButton = this.querySelector('share-button');
        if (shareButton && typeof shareButton.updateUrl === 'function') {
          shareButton.updateUrl(`${window.shopUrl}${relativePath}`);
        }

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', relativePath);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        // FAST PATH: when variants share the gallery (e.g. waist/length sizes
        // of the same color show the same product photos in the same order),
        // skip the 3-loop reconcile + the modal innerHTML rewrite entirely —
        // only call setActiveMedia below. This is the common JNCO PDP case
        // (cross-color is a separate product navigation, not a variant
        // switch) and drops updateMedia from ~30-60ms to ~1-2ms.
        //
        // SLOW PATH stays in place for any case where source and destination
        // galleries legitimately differ (theme-editor variant swap, future
        // per-variant media setups, etc.).
        let galleryMatches = false;
        if (mediaGallerySource && mediaGalleryDestination) {
          const sourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const destItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
          galleryMatches =
            sourceItems.length === destItems.length &&
            sourceItems.every((li, i) => li.dataset.mediaId === destItems[i].dataset.mediaId);
        }

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (!galleryMatches && mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // Always: set the featured media as active in the gallery. Cheap (~1ms),
        // just toggles .is-active and moves the active <li> to first. Bound
        // to setActiveMedia in assets/media-gallery.js.
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // Modal update: only when the gallery actually changed. When variants
        // share the gallery the modal content is identical, so the innerHTML
        // rewrite is wasted (and forces a sync style/layout recalc).
        if (!galleryMatches) {
          const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
          const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
          if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
